import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';

import type { GetAllTrayCapacitiesQuery } from 'data/__generated__';
import { textColumn } from 'components/DataTable';
import { PercentageBar } from 'components/PercentageBar';

export type TrayCapacityRow = GetAllTrayCapacitiesQuery['allTrayCapacities'][number];

type TrayCapacityFilterInput = {
    searchValue: string;
    maxPercent: number;
};

export const trayCapacitiesColumns: ColumnDef<TrayCapacityRow, unknown>[] = [
    textColumn<TrayCapacityRow, TrayCapacityFilterInput>({
        id: 'siteName',
        accessorFn: (row) => row.microstoreColumn?.siteName ?? 'Sans site',
        header: 'Site',
        defaultVisible: true,
    }),
    textColumn<TrayCapacityRow, TrayCapacityFilterInput>({
        id: 'machineName',
        accessorFn: (row) => row.microstoreColumn?.name ?? row.microstoreColumnId,
        header: 'Machine',
        defaultVisible: true,
    }),
    textColumn<TrayCapacityRow, TrayCapacityFilterInput>({
        id: 'templateName',
        accessorFn: (row) => row.microstoreColumn?.microstoreColumnTemplateName ?? '',
        header: 'Template',
        defaultVisible: true,
    }),
    {
        ...textColumn<TrayCapacityRow, TrayCapacityFilterInput>({
            id: 'trays',
            accessorFn: () => '',
            header: 'Bacs',
            defaultVisible: true,
            filterKey: 'maxPercent',
            placeholder: 'Seuil max (%)',
            cell: ({ row }) => {
                const trays = row.original.locations.map((loc) => {
                    const maxCapacity = loc.microstoreLocation?.microstoreLocationTemplate?.trayMaxCapacity ?? 0;
                    const percent = maxCapacity > 0 ? Math.round((loc.currentLevel / maxCapacity) * 100) : 0;
                    return {
                        productName: loc.product?.fullname ?? loc.product?.name ?? loc.productId,
                        currentLevel: loc.currentLevel,
                        maxCapacity,
                        percent,
                    };
                });

                return (
                    <TraysContainer>
                        {trays.map((tray, idx) => (
                            <TrayItem key={idx}>
                                <TrayInfo>
                                    <TrayName>{tray.productName}</TrayName>
                                    <PercentageBar
                                        label={`${tray.currentLevel}/${tray.maxCapacity}g`}
                                        percent={tray.percent}
                                    />
                                </TrayInfo>
                            </TrayItem>
                        ))}
                    </TraysContainer>
                );
            },
        }),
        filterFn: (row, _columnId, threshold: number) => {
            return row.original.locations.some((loc) => {
                const max = loc.microstoreLocation?.microstoreLocationTemplate?.trayMaxCapacity ?? 0;
                const percent = max > 0 ? (loc.currentLevel / max) * 100 : 0;
                return percent <= threshold;
            });
        },
    },
];

const TraysContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TrayItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TrayInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const TrayName = styled.div`
    font-size: 13px;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
